/**
 * Per-user "most recently used" project order. Server-synced so it works
 * across devices / browsers — the projects list response carries
 * `lastOpenedAt` per project, and every board visit POSTs to
 * `/projects/:id/touch` which upserts the timestamp.
 *
 * Clicks update the React Query cache optimistically so the sidebar
 * reorders immediately, without waiting for the network round trip.
 */

import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { Project } from '@vd/types';
import { api } from '../../lib/api';

/**
 * Sort projects by most-recently-opened-by-this-user first. Projects with
 * no `lastOpenedAt` fall back to `lastActivityAt`, then `createdAt`, so a
 * fresh account still sees a reasonable ordering.
 */
export function orderByMru<T extends { lastOpenedAt?: string; lastActivityAt?: string; createdAt?: string }>(
  projects: T[],
): T[] {
  return [...projects].sort((a, b) => {
    const aKey = a.lastOpenedAt ?? '';
    const bKey = b.lastOpenedAt ?? '';
    if (aKey !== bKey) return bKey.localeCompare(aKey);
    // Tiebreak deterministically so order is stable in empty-MRU state.
    const aFallback = a.lastActivityAt ?? a.createdAt ?? '';
    const bFallback = b.lastActivityAt ?? b.createdAt ?? '';
    return bFallback.localeCompare(aFallback);
  });
}

/**
 * The sidebar's saved project list after a project has left it (deleted, or
 * access lost): the ones still here, in their saved order, then the most
 * recently opened of the rest at the bottom, until `limit` (Jason, 17 Oct).
 * Without the refill, deleting a project took a full list of six down to five
 * while more projects were there to show.
 */
export function refillSidebarIds<T extends { id: string; lastOpenedAt?: string; lastActivityAt?: string; createdAt?: string }>(
  kept: string[],
  projects: T[],
  limit: number,
): string[] {
  const shown = new Set(kept);
  const rest = orderByMru(projects).filter((p) => !shown.has(p.id)).map((p) => p.id);
  return [...kept, ...rest].slice(0, Math.max(kept.length, limit));
}

/**
 * Hook that returns a `touch` function. Call it whenever the user opens a
 * project — the returned function:
 *   1. Optimistically bumps the project's `lastOpenedAt` in every cached
 *      `['projects', companyId]` query so the sidebar reorders instantly.
 *   2. Fires `POST /projects/:id/touch` in the background.
 *   3. On success, invalidates so the real server time replaces the
 *      optimistic one (close enough that the ordering doesn't flicker).
 */
export function useTouchProject(): (projectId: string) => void {
  const qc = useQueryClient();
  // Stable identity across renders so callers can put it in effect deps
  // without thrashing.
  return useCallback((projectId: string) => {
    if (!projectId) return;
    const now = new Date().toISOString();
    // Every `['projects', ...]` cache entry gets patched — there may be
    // several if multiple companies are loaded.
    qc.setQueriesData<Project[]>({ queryKey: ['projects'] }, (prev) => {
      if (!prev) return prev;
      let changed = false;
      const next = prev.map((p) => {
        if (p.id !== projectId) return p;
        changed = true;
        return { ...p, lastOpenedAt: now };
      });
      return changed ? next : prev;
    });
    // Fire-and-forget — network failure just means the next page load
    // will have the old ordering. No toast; this is a silent convenience.
    void api.touchProject(projectId).catch(() => {});
  }, [qc]);
}
